// Real API service for Ghana Town Membership System

import { API_CONFIG } from '../config/api';
import type {
  Member,
  Contribution,
  Event,
  EventAttendance,
  Town,
  Expense,
  Benefit,
  User
} from '../types/index';

const API_BASE_URL = API_CONFIG.BASE_URL;

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface LoginResponse {
  user: {
    id: string;
    username: string;
    name: string;
    role: string;
    permissions: string[];
    townId: string | null;
  };
  token: string;
  refreshToken?: string;
  expiresIn: number;
  tokenType: string;
}

class ApiClient {
  private getAuthHeaders(): HeadersInit {
    // Try both token storage methods for backward compatibility
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      const fullUrl = `${API_BASE_URL}${endpoint}`;
      const headers = this.getAuthHeaders() as Record<string, string>;


      const response = await fetch(fullUrl, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers
        }
      });



      // Handle no content
      if (response.status === 204) {
        return { success: true } as ApiResponse<T>;
      }

      const data = await response.json() as ApiResponse<T>;



      // Handle authentication errors by clearing invalid tokens
      if (!data.success && response.status === 401) {
        console.warn('Authentication failed for endpoint:', endpoint);
        // Only clear token if it's strictly auth failure, not just some permission issue
        if (data.error?.includes('Unauthorized') || data.error?.includes('token')) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_data');

          // Only reload if we're not already on the login page and not in maintenance mode
          if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/maintenance')) {
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        }
      }

      return data;
    } catch (error) {
      console.error('API request error:', error);
      return { success: false, error: 'Network error' };
    }
  }

  async download(endpoint: string, options: RequestInit = {}): Promise<Blob | null> {
    try {
      const fullUrl = `${API_BASE_URL}${endpoint}`;
      const response = await fetch(fullUrl, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers
        }
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      return await response.blob();
    } catch (error) {
      console.error('API download error:', error);
      return null;
    }
  }
}

const apiClient = new ApiClient();

// Member API
export const memberApi = {
  async getAll(): Promise<ApiResponse<Member[]>> {
    // Get user data to determine role for limit parameter
    const userData = localStorage.getItem('user_data');
    let limitParam = '';
    if (userData) {
      try {
        const user = JSON.parse(userData);
        // Set high limit for admin roles to fetch all members
        if (['admin', 'member_admin', 'town_admin'].includes(user.role)) {
          limitParam = '?limit=10000';
        }
      } catch (error) {
        console.warn('Failed to parse user data for member limit:', error);
      }
    }

    const response = await apiClient.request<Member[]>(`/api/members${limitParam}`);

    return response;
  },

  async create(memberData: Partial<Member>): Promise<ApiResponse<Member>> {
    return apiClient.request('/api/members', {
      method: 'POST',
      body: JSON.stringify(memberData)
    });
  },

  async update(id: string, memberData: Partial<Member>): Promise<ApiResponse<Member>> {
    return apiClient.request(`/api/members/${id}`, {
      method: 'PUT',
      body: JSON.stringify(memberData)
    });
  },

  async delete(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.request(`/api/members/${id}`, {
      method: 'DELETE'
    });
  },

  async flagDeceased(memberId: string, deceasedDate: string, notes?: string): Promise<ApiResponse<Member>> {
    return apiClient.request('/api/members/flag-deceased', {
      method: 'POST',
      body: JSON.stringify({ memberId, deceasedDate, notes })
    });
  },

  async generateFinancialReport(memberId: string, format: 'pdf' | 'csv' = 'pdf'): Promise<Blob | null> {
    return apiClient.download('/api/members/generate-financial-report', {
      method: 'POST',
      body: JSON.stringify({ memberId, format })
    });
  }
};

// Contribution API
export const contributionApi = {
  async getAll(): Promise<ApiResponse<Contribution[]>> {
    return apiClient.request('/api/contributions');
  },

  async create(contributionData: Partial<Contribution>): Promise<ApiResponse<Contribution>> {
    return apiClient.request('/api/contributions', {
      method: 'POST',
      body: JSON.stringify(contributionData)
    });
  },

  async approve(id: string, approvedBy: string, userName: string): Promise<ApiResponse<Contribution>> {
    return apiClient.request(`/api/contributions/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify({ approvedBy, userName })
    });
  },

  async reject(id: string, rejectedBy: string, userName: string, reason: string): Promise<ApiResponse<Contribution>> {
    return apiClient.request(`/api/contributions/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ rejectedBy, userName, reason })
    });
  },

  async processPayment(paymentData: Record<string, unknown>): Promise<ApiResponse<Contribution>> {
    return apiClient.request('/api/payments/process', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  }
};

// Event API
export const eventApi = {
  async getAll(): Promise<ApiResponse<Event[]>> {
    return apiClient.request('/api/events');
  },

  async create(eventData: Partial<Event>): Promise<ApiResponse<Event>> {
    return apiClient.request('/api/events', {
      method: 'POST',
      body: JSON.stringify(eventData)
    });
  },

  async updateAttendance(eventId: string, attendance: EventAttendance[]): Promise<ApiResponse<boolean>> {
    return apiClient.request(`/api/events/${eventId}/attendance`, {
      method: 'PUT',
      body: JSON.stringify(attendance)
    });
  },

  async rsvp(eventId: string, memberId: string, status: 'present' | 'absent'): Promise<ApiResponse<boolean>> {
    return apiClient.request(`/api/events/${eventId}/rsvp`, {
      method: 'POST',
      body: JSON.stringify({ memberId, status })
    });
  }
};

// Town API
export const townApi = {
  async getAll(): Promise<ApiResponse<Town[]>> {
    return apiClient.request('/api/towns');
  },

  async create(townData: Partial<Town>): Promise<ApiResponse<Town>> {
    return apiClient.request('/api/towns', {
      method: 'POST',
      body: JSON.stringify(townData)
    });
  },

  async update(id: string, townData: Partial<Town>): Promise<ApiResponse<Town>> {
    return apiClient.request(`/api/towns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(townData)
    });
  },

  async delete(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.request(`/api/towns/${id}`, {
      method: 'DELETE'
    });
  }
};

// Expense API
export const expenseApi = {
  async getAll(): Promise<ApiResponse<Expense[]>> {
    return apiClient.request('/api/expenses');
  },

  async create(expenseData: Partial<Expense>): Promise<ApiResponse<Expense>> {
    return apiClient.request('/api/expenses', {
      method: 'POST',
      body: JSON.stringify(expenseData)
    });
  },

  async approve(id: string, approvedBy: string, userName: string): Promise<ApiResponse<Expense>> {
    return apiClient.request(`/api/expenses/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify({ approvedBy, userName })
    });
  },

  async reject(id: string, rejectedBy: string, userName: string, reason: string): Promise<ApiResponse<Expense>> {
    return apiClient.request(`/api/expenses/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ rejectedBy, userName, reason })
    });
  }
};

// Benefits API
export const benefitApi = {
  async getAll(): Promise<ApiResponse<Benefit[]>> {
    return apiClient.request('/api/benefits');
  },

  async create(benefitData: Partial<Benefit>): Promise<ApiResponse<Benefit>> {
    return apiClient.request('/api/benefits', {
      method: 'POST',
      body: JSON.stringify(benefitData)
    });
  },

  async approve(id: string, approvedBy: string, userName: string): Promise<ApiResponse<Benefit>> {
    return apiClient.request(`/api/benefits/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify({ approvedBy, userName })
    });
  },

  async reject(id: string, rejectedBy: string, userName: string, reason: string): Promise<ApiResponse<Benefit>> {
    return apiClient.request(`/api/benefits/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ rejectedBy, userName, reason })
    });
  },

  async markPaid(id: string, reference: string): Promise<ApiResponse<Benefit>> {
    return apiClient.request(`/api/benefits/${id}/pay`, {
      method: 'PUT',
      body: JSON.stringify({ reference })
    });
  }
};

// Audit API
export const auditApi = {
  async getAll(): Promise<ApiResponse<Record<string, unknown>[]>> {
    return apiClient.request('/api/audit-logs');
  },

  async export(filters: Record<string, unknown>): Promise<ApiResponse<string>> {
    return apiClient.request('/api/audit-logs/export', {
      method: 'POST',
      body: JSON.stringify(filters)
    });
  }
};

// SMS API
export const smsApi = {
  async sendBulk(smsData: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/sms/send', {
      method: 'POST',
      body: JSON.stringify(smsData)
    });
  },

  async sendCredentials(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/sms/send-credentials', {
      method: 'POST'
    });
  },

  async getHistory(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/sms/history');
  },

  async getSendHistory(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/sms/send-history');
  },

  async getTemplates(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/sms/templates');
  },

  async getStatistics(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/sms/statistics');
  },

  async getBatchedHistory(filters?: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    const queryString = params.toString();
    return apiClient.request(`/api/sms/batched-history${queryString ? `?${queryString}` : ''}`);
  }
};

// Email API
export const emailApi = {
  async sendBulk(emailData: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/email/send', {
      method: 'POST',
      body: JSON.stringify(emailData)
    });
  },

  async getHistory(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/email/history');
  }
};

// Template Management API
export const templateApi = {
  async getAll(type?: 'sms' | 'email'): Promise<ApiResponse<Record<string, unknown>[]>> {
    const query = type ? `?type=${type}` : '';
    return apiClient.request(`/api/templates${query}`);
  },

  async get(id: string): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request(`/api/templates/${id}`);
  },

  async create(data: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/templates', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async update(id: string, data: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request(`/api/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async delete(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.request(`/api/templates/${id}`, {
      method: 'DELETE'
    });
  }
};

// Report API
export const reportApi = {
  async generateExcel(reportType: string, filters: Record<string, unknown>): Promise<ApiResponse<string>> {
    return apiClient.request('/api/reports/excel', {
      method: 'POST',
      body: JSON.stringify({ reportType, filters })
    });
  }
};

// Mobile Money API Integration
export const momoApi = {
  async initiatePayment(paymentData: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/payments/initiate', {
      method: 'POST',
      body: JSON.stringify(paymentData)
    });
  },

  async checkPaymentStatus(transactionId: string): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request(`/api/payments/status/${transactionId}`);
  }
};

// Settings API
export const settingsApi = {
  async get(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/settings');
  },

  async update(settings: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  },

  async patch(settings: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings)
    });
  },

  async getNotifications(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/settings/notifications');
  },

  async updateNotifications(settings: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/settings/notifications', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  },

  async testNotifications(service: string, testMessage?: string, testEmail?: string): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/settings/notifications/test', {
      method: 'POST',
      body: JSON.stringify({ service, testMessage, testEmail })
    });
  },

  async testSMS(config?: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/sms/test', {
      method: 'POST',
      body: config ? JSON.stringify(config) : undefined
    });
  },

  async getSMSStatus(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/sms/status');
  },

  async getSMSDebugConfig(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/sms/debug-config');
  },

  async getPayments(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/settings/payments');
  },

  async updatePayments(settings: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/settings/payments', {
      method: 'PUT',
      body: JSON.stringify(settings)
    });
  },

  async getDatabaseStats(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/stats/database');
  }
};

// Brand API
export const brandApi = {
  async get(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/brand');
  },

  async update(brandData: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/brand', {
      method: 'PUT',
      body: JSON.stringify(brandData)
    });
  },

  async reset(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/brand/reset', {
      method: 'POST'
    });
  }
};

// Dashboard API
export const dashboardApi = {
  async getStats(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/dashboard/stats');
  },

  async getApprovalsSummary(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/approvals/summary');
  }
};

// System API (for developers)
export const systemApi = {
  async getHealth(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/health');
  },

  async getUsers(): Promise<ApiResponse<User[]>> {
    return apiClient.request('/api/system/users');
  },

  async updateUser(id: string, userData: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request(`/api/system/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  },

  async deleteUser(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.request(`/api/system/users/${id}`, {
      method: 'DELETE'
    });
  },

  async getAdmins(): Promise<ApiResponse<User[]>> {
    return apiClient.request('/api/system/admins');
  },

  async createAdmin(adminData: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/admins', {
      method: 'POST',
      body: JSON.stringify(adminData)
    });
  },

  async securityScan(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/security-scan', {
      method: 'POST'
    });
  },

  async logoutAllUsers(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/logout-all', {
      method: 'POST'
    });
  },

  async disableUser(userId: string): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request(`/api/system/disable-user/${userId}`, {
      method: 'POST'
    });
  },

  async freshStart(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/fresh-start', {
      method: 'POST'
    });
  },

  async dataCleanup(type: string): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/data-cleanup', {
      method: 'POST',
      body: JSON.stringify({ type })
    });
  },

  async getAdminTools(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/admin-tools');
  },

  async getMaintenanceStatus(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/maintenance-status');
  },

  async setMaintenanceMode(enabled: boolean, message?: string, estimatedCompletion?: string): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/maintenance-mode', {
      method: 'POST',
      body: JSON.stringify({ enabled, message, estimatedCompletion })
    });
  },

  async createBackup(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/backup', {
      method: 'POST'
    });
  },

  async restoreBackup(backupData: Record<string, unknown>): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/restore', {
      method: 'POST',
      body: JSON.stringify({ backupData })
    });
  },

  async maintenance(action: string): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/maintenance', {
      method: 'POST',
      body: JSON.stringify({ action })
    });
  },

  async getConfig(): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/config');
  },

  async updateConfig(key: string, value: string, description?: string): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/system/config', {
      method: 'PUT',
      body: JSON.stringify({ key, value, description })
    });
  }
};

// File Upload API
export const uploadApi = {
  async uploadFile(file: File, type: string): Promise<ApiResponse<Record<string, unknown>>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    return apiClient.request('/api/upload', {
      method: 'POST',
      body: formData,
      headers: {} // Let browser set content-type for FormData
    });
  }
};

// Bulk Operations API
export const bulkApi = {
  async approveContributions(contributionIds: string[], approvedBy: string, userName: string): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/bulk/approve-contributions', {
      method: 'POST',
      body: JSON.stringify({ contributionIds, approvedBy, userName })
    });
  }
};

// Admin API
export const adminApi = {
  async getAll(): Promise<ApiResponse<User[]>> {
    return apiClient.request('/api/admins');
  },

  async create(adminData: Partial<User>): Promise<ApiResponse<User>> {
    return apiClient.request('/api/admins', {
      method: 'POST',
      body: JSON.stringify(adminData)
    });
  },

  async delete(id: string): Promise<ApiResponse<boolean>> {
    return apiClient.request(`/api/system/users/${id}`, {
      method: 'DELETE'
    });
  },

  async resetPassword(id: string, newPassword?: string): Promise<ApiResponse<boolean>> {
    return apiClient.request(`/api/admins/${id}/reset-password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword })
    });
  }
};

// Authentication API
export const authApi = {
  async login(credentials: { username: string; password: string; role?: 'admin' | 'member' | 'developer' }): Promise<ApiResponse<LoginResponse>> {
    const response = await apiClient.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });

    if (response.success && response.data?.token) {
      // Ensure consistent token storage
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('token', response.data.token); // Also store as 'token' for backward compatibility
      localStorage.setItem('user_data', JSON.stringify(response.data.user));
      if (response.data.refreshToken) {
        localStorage.setItem('refresh_token', response.data.refreshToken);
      }
    }

    return response;
  },

  async register(userData: Partial<User>): Promise<ApiResponse<User>> {
    return apiClient.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  },

  async logout(): Promise<ApiResponse<boolean>> {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token'); // Also remove 'token' for backward compatibility
    localStorage.removeItem('refresh_token');
    return { success: true, data: true };
  },

  async verifyToken(token: string): Promise<ApiResponse<Record<string, unknown>>> {
    try {
      return await apiClient.request('/api/auth/verify', {
        method: 'POST',
        body: JSON.stringify({ token })
      });
    } catch (_error) {
      // If verification fails, just return failure instead of crashing
      return { success: false, error: 'Token verification failed' };
    }
  },

  async refreshToken(refreshToken: string): Promise<ApiResponse<Record<string, unknown>>> {
    try {
      return await apiClient.request('/api/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken })
      });
    } catch (_error) {
      return { success: false, error: 'Token refresh failed' };
    }
  },

  async switchToMemberPortal(adminToken: string, selectedTownId: string, memberId?: string): Promise<ApiResponse<Record<string, unknown>>> {
    try {
      return await apiClient.request('/api/auth/switch-to-member', {
        method: 'POST',
        body: JSON.stringify({ adminToken, selectedTownId, memberId })
      });
    } catch (_error) {
      return { success: false, error: 'Failed to switch to member portal' };
    }
  },

  async switchToAdminPortal(userToken: string, selectedAdminRole: string): Promise<ApiResponse<Record<string, unknown>>> {
    try {
      return await apiClient.request('/api/auth/switch-to-admin', {
        method: 'POST',
        body: JSON.stringify({ userToken, selectedAdminRole })
      });
    } catch (_error) {
      return { success: false, error: 'Failed to switch to admin portal' };
    }
  },

  async changePassword(passwordData: { currentPassword: string; newPassword: string }): Promise<ApiResponse<Record<string, unknown>>> {
    return apiClient.request('/api/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(passwordData)
    });
  }
};

// Stats API
export const statsApi = {
  async getDatabaseStats(townId?: string, startDate?: string, endDate?: string): Promise<ApiResponse<Record<string, unknown>>> {
    const params = new URLSearchParams();
    if (townId && townId !== 'all') params.append('townId', townId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const queryString = params.toString();
    return apiClient.request(`/api/stats/database${queryString ? `?${queryString}` : ''}`);
  },

  async getTimeSeries(townId?: string, startDate?: string, endDate?: string, interval?: string): Promise<ApiResponse<Record<string, unknown>>> {
    const params = new URLSearchParams();
    if (townId && townId !== 'all') params.append('townId', townId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (interval) params.append('interval', interval);
    const queryString = params.toString();
    return apiClient.request(`/api/stats/time-series${queryString ? `?${queryString}` : ''}`);
  },

  async getAggregations(townId?: string, startDate?: string, endDate?: string, metric?: string): Promise<ApiResponse<Record<string, unknown>>> {
    const params = new URLSearchParams();
    if (townId && townId !== 'all') params.append('townId', townId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (metric) params.append('metric', metric);
    const queryString = params.toString();
    return apiClient.request(`/api/stats/aggregations${queryString ? `?${queryString}` : ''}`);
  },

  async getComparative(period1Start?: string, period1End?: string, period2Start?: string, period2End?: string, townId?: string, compareType?: string): Promise<ApiResponse<Record<string, unknown>>> {
    const params = new URLSearchParams();
    if (period1Start) params.append('period1Start', period1Start);
    if (period1End) params.append('period1End', period1End);
    if (period2Start) params.append('period2Start', period2Start);
    if (period2End) params.append('period2End', period2End);
    if (townId && townId !== 'all') params.append('townId', townId);
    if (compareType) params.append('compareType', compareType);
    const queryString = params.toString();
    return apiClient.request(`/api/stats/comparative${queryString ? `?${queryString}` : ''}`);
  }
};

// Main API object for general use
export const api = {
  get: <T>(endpoint: string) => apiClient.request<T>(endpoint),
  post: <T>(endpoint: string, data?: unknown) => apiClient.request<T>(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined
  }),
  put: <T>(endpoint: string, data?: unknown) => apiClient.request<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined
  }),
  delete: <T>(endpoint: string) => apiClient.request<T>(endpoint, {
    method: 'DELETE'
  })
};
